import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";

// material-ui
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  ButtonBase,
  Collapse,
  Divider,
  List,
  ListSubheader,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

// project imports
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import NavCollapse from "../NavCollapse";
import NavItem from "../NavItem";

// ==============================|| SIDEBAR MENU LIST GROUP ||============================== //
// Every group header is a toggle: click it to open or close the group's items. A group
// starts open unless its definition says `defaultOpen: false` (Administration), and that
// default is honoured on load even when the landing page lives inside the group. After
// load, navigating into a closed group opens it, so a link from elsewhere never lands on
// a hidden item. The collapsed rail (menu minimised) ignores all of this and shows every
// item, since the headers are not rendered there.

/**
 * Open/closed per group id for the life of the page. The Sidebar renders the menu under a
 * different parent when the drawer toggles (overlay on small screens, rail on desktop), which
 * remounts every group; without this, a group the user opened would snap back to its default
 * on each toggle. Cleared on a full reload, which is where "starts closed" applies.
 */
export const navGroupOpenState = new Map();

/** True when one of the group's items is the page at `pathname`. */
export const groupContainsPath = (group, pathname) =>
  Boolean(
    pathname &&
    group?.children?.some((child) => {
      const urls = child.url ? [child.url] : [];
      child.children?.forEach((sub) => sub.url && urls.push(sub.url));
      return urls.some(
        (url) => pathname === url || pathname.startsWith(`${url}/`),
      );
    }),
  );

const NavGroup = ({ item }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const leftDrawerOpened = useSelector((state) => state.session.menuVisible);
  const siteConfigs = useSelector((state) => state.administrator.siteConfigs);

  const collapsible = item.collapsible !== false;
  const [open, setOpenState] = useState(() =>
    navGroupOpenState.has(item.id) ? navGroupOpenState.get(item.id) : item.defaultOpen !== false
  );
  const setOpen = useCallback(
    (next) => {
      setOpenState((current) => {
        const value = typeof next === "function" ? next(current) : next;
        navGroupOpenState.set(item.id, value);
        return value;
      });
    },
    [item.id]
  );

  // Navigating into a closed group after load opens it. Only a change of path counts, so
  // a closed-by-default group stays closed on load whatever the landing page (and under
  // StrictMode's doubled mount effects), while a later link back to that same page opens it.
  const previousPath = useRef(pathname);
  useEffect(() => {
    if (previousPath.current === pathname) {
      return;
    }
    previousPath.current = pathname;
    if (groupContainsPath(item, pathname)) {
      setOpen(true);
    }
  }, [item, pathname, setOpen]);

  // menu list collapse & items
  const items = item.children
    ?.filter((menu) => {
      // Filter out items based on hidden condition
      if (typeof menu.hidden === "function") {
        // Default to hidden while configs are still loading to prevent flash
        if (!siteConfigs?.siteConfigs) {
          return false;
        }
        return !menu.hidden(siteConfigs.siteConfigs);
      }
      return true;
    })
    .map((menu) => {
      switch (menu.type) {
        case "collapse":
          return <NavCollapse key={menu.id} menu={menu} level={1} />;
        case "item":
          return <NavItem key={menu.id} item={menu} level={1} />;
        default:
          return (
            <Typography key={menu.id} variant="h6" color="error" align="center">
              Menu Items Error
            </Typography>
          );
      }
    });

  const showHeader = leftDrawerOpened && item.title;
  const expanded = !leftDrawerOpened || !collapsible || open;
  const panelId = `nav-group-${item.id}-items`;

  const headerText = (
    <Typography
      variant="caption"
      component="span"
      sx={{ ...theme.typography.menuCaption, flex: 1, textAlign: "left" }}
      display="block"
      gutterBottom
    >
      {item.title}
      {item.caption && (
        <Typography
          variant="caption"
          sx={{ ...theme.typography.subMenuCaption }}
          display="block"
          gutterBottom
        >
          {item.caption}
        </Typography>
      )}
    </Typography>
  );

  // ListSubheader renders an <li>, which is what a <ul> may contain; the toggle sits inside it.
  const header = showHeader ? (
    collapsible ? (
      <ListSubheader
        disableSticky
        disableGutters
        sx={{
          p: 0,
          lineHeight: "inherit",
          bgcolor: "transparent",
          color: "inherit",
        }}
      >
        <ButtonBase
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={panelId}
          data-testid={`nav-group-toggle-${item.id}`}
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "flex-start",
            justifyContent: "space-between",
            borderRadius: 1,
            pr: 1,
            "&:hover": { backgroundColor: theme.palette.action.hover },
          }}
        >
          {headerText}
          {open ? (
            <ExpandLess
              fontSize="small"
              sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
            />
          ) : (
            <ExpandMore
              fontSize="small"
              sx={{ color: theme.palette.text.secondary, mt: 0.5 }}
            />
          )}
        </ButtonBase>
      </ListSubheader>
    ) : (
      <ListSubheader
        disableSticky
        disableGutters
        sx={{
          p: 0,
          lineHeight: "inherit",
          bgcolor: "transparent",
          color: "inherit",
        }}
      >
        {headerText}
      </ListSubheader>
    )
  ) : null;

  return (
    <>
      <List
        sx={!leftDrawerOpened ? { width: "100%" } : undefined}
        subheader={header}
      >
        {/* The id lives on a wrapper that stays mounted, so aria-controls always resolves even while closed. */}
        <div id={panelId}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            {items}
          </Collapse>
        </div>
      </List>

      {/* group divider */}
      <Divider sx={{ mt: 0.25, mb: 1.25 }} />
    </>
  );
};

NavGroup.propTypes = {
  item: PropTypes.object,
};

export default NavGroup;
